import { Mail, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/wave-wire-logo.jpg";
import { useIsMobile } from "@/hooks/use-mobile";

const Footer = () => {
  const isMobile = useIsMobile();

  const navLinks = [
    { label: "Portfolio", href: "/portfolio" },
    { label: "Process", href: "/how-it-works-web-design-process" },
    { label: "Pricing", href: "/services-pricing" },
    { label: "Contact", href: "/contact" },
    { label: "Blog", href: "/blog" },
    { label: "About", href: "/about" },
  ];

  if (isMobile) {
    return (
      <footer className="py-8 border-t border-border bg-card/30">
        <div className="container mx-auto px-4">
          {/* Mobile: 2-column layout */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Left Side - Logo, Subtext, Contact */}
            <div className="flex flex-col">
              {/* Logo & Name */}
              <div className="flex items-center gap-2 mb-2">
                <img
                  src={logo}
                  alt="Wave & Wire Web Design Hamptons"
                  className="h-8 w-8 rounded-full object-cover ring-2 ring-primary/30"
                />
                <span className="text-xs font-bold tracking-tight">WAVE & WIRE</span>
              </div>
              
              {/* Subtext */}
              <p className="text-[10px] text-muted-foreground mb-3 leading-relaxed">
                Premium Hamptons web design without the agency price tag.
              </p>
              
            </div>

            {/* Right Side - Quick Links in 2 columns */}
            <div>
              <h4 className="text-[10px] font-bold mb-2 text-primary uppercase tracking-wider">Quick Links</h4>
              <div className="grid grid-cols-2 gap-x-2">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[10px] text-muted-foreground hover:text-primary transition-colors min-h-6 flex items-center"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Get In Touch - Centered with side-by-side contact */}
          <div className="text-center mb-6">
            <h4 className="text-[10px] font-bold mb-2 text-primary uppercase tracking-wider">Get In Touch</h4>
            <div className="flex justify-center gap-4">
              <a
                href="tel:+16317070672"
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-primary transition-colors min-h-6"
              >
                <Phone size={10} />
                (631) 707-0672
              </a>
              <a
                href="mailto:aj@waveandwirewebdesigns.com?subject=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
                className="flex items-center gap-1.5 text-[10px] text-muted-foreground hover:text-primary transition-colors min-h-6"
              >
                <Mail size={10} />
                <span className="truncate">aj@waveandwire...</span>
              </a>
            </div>
          </div>

          {/* Bottom */}
          <div className="pt-4 border-t border-border/50 text-center">
            <p className="text-[10px] text-muted-foreground">
              © {new Date().getFullYear()} Wave & Wire Web Design. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    );
  }

  // Desktop layout
  return (
    <footer className="py-12 border-t border-border bg-card/30">
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div className="flex flex-col items-center md:items-start">
            <div className="flex items-center gap-3 mb-3">
              <img
                src={logo}
                alt="Wave & Wire Web Design Hamptons"
                className="h-10 w-10 rounded-full object-cover ring-2 ring-primary/30"
              />
              <span className="font-bold tracking-tight">WAVE & WIRE</span>
            </div>
            <p className="text-sm text-muted-foreground text-center md:text-left">
              Premium Hamptons web design without the agency price tag.
            </p>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="font-bold mb-3 text-primary">Get In Touch</h4>
            <div className="space-y-2">
              <a
                href="tel:+16317070672"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone size={16} />
                (631) 707-0672
              </a>
              <a
                href="mailto:aj@waveandwirewebdesigns.com?subject=I'd%20Like%20To%20Get%20Started%20on%20my%20Own%20Personal%20Website"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Mail size={16} />
                aj@waveandwirewebdesigns.com
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-col items-center md:items-start">
            <h4 className="font-bold mb-3 text-primary">Quick Links</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-6 border-t border-border/50 text-center">
          <p className="text-sm text-muted-foreground mb-2">
            © {new Date().getFullYear()} Wave & Wire Web Design Hamptons. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground">
            Custom web design for Hamptons businesses | Affordable small business websites | Southampton, East Hampton, Montauk, Sag Harbor & beyond
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;